import React, { useEffect, useRef } from "react";
import { CatalogStep } from "./steps/CatalogStep";
import { Step } from "./steps/Step";
import { useStep } from "../../context/stepContext";
import { ContextWrapper, ContextWrapperProps } from "../ContextWrapper";
import { ChairModel, useChair } from "../../context/ChairContext";
import { ModelViewer } from "./modelViewer/ModelViewer";
import { OverviewStep } from "./steps/OverviewStep";
import { CurrentSelection } from "./steps/CurrentSelection";
import { useGetChairConfiguratorInfo } from "../../api/hooks/useChairs";
import { FrameStep } from "./steps/FrameStep";
import { VariantStep } from "./steps/VariantStep";
import { AccessoryStep } from "./steps/AccessoryStep";
import { useLocalStorage } from "../../utilities/hooks";
import { CartItem, useCart } from "../../context/CartContext";
import { CartStep } from "./steps/CartStep";
import { APP_NAME } from "../../utilities/consts";
import { ThankYou } from "./steps/ThankYou";
import { CoverStep } from "./steps/CoverStep";

const Configurator = () => {
    const { step, setStep } = useStep();
    const { chair, setChair, reset } = useChair();
    const { cart, setCart, loadFromCart, setLoadFromCart } = useCart();
    const [localChair, setLocalChair] = useLocalStorage<ChairModel>(`${APP_NAME}.chair`, { id: "" });
    const [localId, setLocalId] = useLocalStorage<string>(`${APP_NAME}.chairId`, "");
    const [localCart, setLocalCart] = useLocalStorage<Array<CartItem>>(`${APP_NAME}.cart`, []);
    const isInitialChairMount = useRef(true);
    const isInitialCartMount = useRef(true);

    const id = new URLSearchParams(window.location.search).get("id") ?? null;
    const isCart = new URLSearchParams(window.location.search).get("cart") === "1";
    const chairConfigInfoQuery = useGetChairConfiguratorInfo({ id: chair.id });

    useEffect(() => {
        // if there is an id in the url and this id is not the same as the id of the chair saved in localstorage we have a new chair to load
        if (id && id !== localChair.id) {
            // and if the state chair id is empty
            if (chair.id === "") {
                setChair({ id: id });
            }
        } else {
            // otherwise we load the chair stored in localstorage
            setChair(localChair);
        }
        // if we don't load the cart and no step is defined
        if (!isCart && !step) {
            // and we have a localChair or id is set via url and is not te same as the localChair id then we go to the overview
            if (localChair.id || (id && id !== localChair.id)) {
                setStep(Step.OVERVIEW);
            } else {
                // otherwise catalog is loaded
                setStep(Step.CATALOG);
            }
        } else if (isCart) {
            // if isCart is true we open the cart
            setStep(Step.CART);
        }
        // if the state cart is empty (on reload) but we have items in localstorage cart we update the cart
        if (cart.length === 0 && localCart.length > 0) {
            setCart(localCart);
        }
    }, []);

    useEffect(() => {
        if (step === Step.OVERVIEW) {
            window.scrollTo(0, 0);
        }
    }, [step]);

    useEffect(() => {
        if (!chairConfigInfoQuery.isFetching && chairConfigInfoQuery.isFetched && chairConfigInfoQuery.isSuccess) {
            const data = chairConfigInfoQuery.data.chairEntries[0] ?? null;
            if (data) {
                const secondary =
                    data.secondaryFrame.length > 0 && data.secondaryFrame[0].secondaryFrameTexture.length > 0
                        ? data.secondaryFrame[0]
                        : null;
                if (loadFromCart) {
                    setLocalId(chair.id);
                    setLoadFromCart(false);
                } else if (localId !== chair.id) {
                    const newChair: ChairModel = {
                        id: chair.id,
                        modelName: data.parent.chairName,
                        modelId: data.parent.id,
                        variant: data.variantName,
                        variantDescription: data.variantDescription,
                        frame: undefined,
                        secondaryFrame: secondary
                            ? { shininess: secondary.shininess, id: secondary.secondaryFrameTexture[0].id }
                            : undefined,
                        cover: undefined,
                        extra: undefined,
                    };
                    reset(newChair);
                    setLocalId(chair.id);
                } else if (chair.modelName === undefined) {
                    setChair(localChair);
                }
            }
        }
    }, [
        chairConfigInfoQuery.isFetching,
        chairConfigInfoQuery.isFetched,
        chairConfigInfoQuery.isSuccess,
        chairConfigInfoQuery.data,
        chair.id,
        localId,
        loadFromCart,
        localChair,
    ]);

    useEffect(() => {
        if (isInitialChairMount.current) {
            isInitialChairMount.current = false;
            return;
        }
        if (chair.id === "") {
            setLocalId("");
        }
        setLocalChair(chair);
    }, [chair]);

    useEffect(() => {
        if (isInitialCartMount.current) {
            isInitialCartMount.current = false;
            return;
        }
        setLocalCart(cart);
    }, [cart]);

    const componentToStep: Map<Step, JSX.Element> = React.useMemo(
        () =>
            new Map([
                [Step.CATALOG, <CatalogStep />],
                [Step.OVERVIEW, <OverviewStep />],
                [Step.VARIANT, <VariantStep />],
                [Step.FRAME, <FrameStep />],
                [Step.COVER, <CoverStep />],
                [Step.ACCESSORY, <AccessoryStep />],
                [Step.CART, <CartStep />],
                [Step.THANK_YOU, <ThankYou />],
            ]),
        []
    );

    return (
        <>
            <div className={"configurator-wrapper"}>
                {step !== Step.CATALOG && step !== Step.CART && step !== Step.THANK_YOU ? (
                    <div className={"background"}>
                        <ModelViewer animate={true} />
                        <>
                            {componentToStep.get(step)}
                            <CurrentSelection />
                        </>
                    </div>
                ) : (
                    componentToStep.get(step)
                )}
            </div>
        </>
    );
};

export const ConfiguratorWrapper = (props: ContextWrapperProps) => {
    return (
        <ContextWrapper {...props}>
            <Configurator />
        </ContextWrapper>
    );
};
